import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaCloud,
  FaCopy,
  FaEdit,
  FaPause,
  FaPlay,
  FaPlus,
  FaSyncAlt,
  FaTimes,
  FaTrash,
} from "react-icons/fa";

const API = "/api/co2";
const PUBLIC_API_BASE = process.env.REACT_APP_CO2_API_BASE_URL || "https://app.carbelim.io";
const UNITS = ["mg", "g", "kg", "t", "mL", "L", "m3", "Pa", "hPa", "kPa", "MPa", "bar", "psi", "ppb", "ppm", "%", "C", "F", "K", "count", "AQI", "mm", "cm", "m", "km", "mA", "A", "mV", "V", "mW", "W", "kW"];
const PERIOD_UNITS = ["day", "week", "month", "year"];
const PERIOD_MINUTES = { day: 1440, week: 10080, month: 43200, year: 525600 };
const TIME_UNITS = ["ms", "s", "min", "hr"];
const TIME_UNIT_MINUTES = { ms: 1 / 60000, s: 1 / 60, min: 1, hr: 60 };
const DAYS = [
  { value: 1, label: "Mon" }, { value: 2, label: "Tue" }, { value: 3, label: "Wed" },
  { value: 4, label: "Thu" }, { value: 5, label: "Fri" }, { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];

const emptyParameter = (name = "") => ({
  name,
  minValue: 0,
  minUnit: name === "o2" ? "mL" : "g",
  maxValue: 1000,
  maxUnit: name === "o2" ? "L" : "kg",
  periodValue: 1,
  periodUnit: "year",
  incrementValue: 1,
  incrementUnit: name === "o2" ? "mL" : "g",
  intervalValue: 5,
  intervalUnit: "min",
});

// Maximum, period, increment and interval depend on each other. They are kept
// in step only when the three value units match, so no conversion is needed.
const linkedRange = (parameter) => {
  if (parameter.minUnit !== parameter.maxUnit || parameter.minUnit !== parameter.incrementUnit) return null;
  const range = Number(parameter.maxValue) - Number(parameter.minValue);
  const intervalMinutes = Number(parameter.intervalValue) * TIME_UNIT_MINUTES[parameter.intervalUnit];
  return range > 0 && intervalMinutes > 0 ? { range, intervalMinutes } : null;
};

// Increment needed to reach maximum within the period (0..increment averages half).
const syncIncrement = (parameter) => {
  const linked = linkedRange(parameter);
  const updates = linked && Number(parameter.periodValue) * PERIOD_MINUTES[parameter.periodUnit] / linked.intervalMinutes;
  if (!(updates >= 1)) return parameter;
  return { ...parameter, incrementValue: Number((2 * linked.range / updates).toPrecision(3)) };
};

// Period it takes to reach maximum with the entered increment.
const syncPeriod = (parameter) => {
  const linked = linkedRange(parameter);
  const increment = Number(parameter.incrementValue);
  if (!linked || !(increment > 0)) return parameter;
  const period = linked.range / (increment / 2) * linked.intervalMinutes / PERIOD_MINUTES[parameter.periodUnit];
  return { ...parameter, periodValue: Number(period >= 1 ? period.toFixed(2) : period.toPrecision(2)) };
};

const updateHint = (parameter) => {
  const increment = Number(parameter.incrementValue);
  if (!linkedRange(parameter) || !(increment > 0)) return null;
  return `Adds a random 0–${increment} ${parameter.incrementUnit} (about ${Number((increment / 2).toPrecision(3))} ${parameter.incrementUnit} on average) every ${parameter.intervalValue} ${parameter.intervalUnit}, reaching ${parameter.maxValue} ${parameter.maxUnit} in about ${parameter.periodValue} ${parameter.periodUnit}.`;
};

const emptyForm = () => ({
  id: null,
  deviceUid: "",
  name: "",
  parameters: [emptyParameter("co2"), emptyParameter("o2")],
  schedule: {
    enabled: true,
    workingDays: [1, 2, 3, 4, 5, 6, 0],
    startTime: "00:00",
    endTime: "23:59",
    utcOffsetMinutes: 330,
  },
  enabled: true,
});

const Co2Devices = () => {
  const token = localStorage.getItem("token");
  const currentRole = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}").role || ""; } catch { return ""; } })();
  const canManage = String(currentRole).toLowerCase().replace(/\s+/g, "_") === "super_admin";
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [copied, setCopied] = useState(null);

  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  const loadDevices = useCallback(async ({ preserveNotice = false } = {}) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/devices`, { headers });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Unable to load CO2 devices");
      setDevices(Array.isArray(data) ? data : []);
      if (!preserveNotice) setNotice(null);
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { loadDevices(); }, [loadDevices]);

  const openCreate = () => {
    setForm(emptyForm());
    setEditorOpen(true);
  };

  const openEdit = (device) => {
    setForm({
      id: device.id,
      deviceUid: device.deviceUid,
      name: device.name,
      parameters: device.parameters.map((parameter) => syncIncrement({ periodValue: 1, periodUnit: "year", intervalValue: 5, intervalUnit: "min", ...parameter })),
      schedule: { enabled: false, ...device.schedule },
      enabled: device.enabled !== false,
    });
    setEditorOpen(true);
  };

  const updateParameter = (index, field, value) => {
    // Editing the increment moves the period; every other value moves the increment.
    const sync = field === "name" ? (parameter) => parameter : field === "incrementValue" ? syncPeriod : syncIncrement;
    setForm((current) => ({
      ...current,
      parameters: current.parameters.map((parameter, parameterIndex) => (
        parameterIndex === index ? sync({ ...parameter, [field]: value }) : parameter
      )),
    }));
  };

  const removeParameter = (index) => {
    setForm((current) => ({
      ...current,
      parameters: current.parameters.filter((_, parameterIndex) => parameterIndex !== index),
    }));
  };

  const saveDevice = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const path = form.id ? `${API}/admin/devices/${form.id}` : `${API}/admin/devices`;
      const response = await fetch(path, {
        method: form.id ? "PUT" : "POST",
        headers,
        body: JSON.stringify(form),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Unable to save CO2 device");
      setEditorOpen(false);
      setNotice({ type: "success", text: `${form.deviceUid} saved` });
      await loadDevices({ preserveNotice: true });
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const togglePause = async (device) => {
    setBusyId(device.id);
    try {
      const response = await fetch(`${API}/admin/devices/${device.id}/state`, {
        method: "POST",
        headers,
        body: JSON.stringify({ paused: !device.paused }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Unable to update device state");
      await loadDevices({ preserveNotice: true });
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    } finally {
      setBusyId(null);
    }
  };

  const deleteDevice = async (device) => {
    if (!window.confirm(`Delete CO2 device ${device.deviceUid}? Its LED API URL will stop working.`)) return;
    setBusyId(device.id);
    try {
      const response = await fetch(`${API}/admin/devices/${device.id}`, { method: "DELETE", headers });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Unable to delete CO2 device");
      setNotice({ type: "success", text: `${device.deviceUid} deleted` });
      await loadDevices({ preserveNotice: true });
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    } finally {
      setBusyId(null);
    }
  };

  const copyUrl = async (deviceUid) => {
    const url = `${PUBLIC_API_BASE}/api/co2/${deviceUid}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(deviceUid);
      setTimeout(() => setCopied((current) => current === deviceUid ? null : current), 1600);
    } catch {
      setNotice({ type: "error", text: "Could not copy the URL" });
    }
  };

  const toggleDay = (day) => {
    setForm((current) => {
      const days = current.schedule.workingDays || [];
      return {
        ...current,
        schedule: {
          ...current.schedule,
          workingDays: days.includes(day) ? days.filter((value) => value !== day) : [...days, day],
        },
      };
    });
  };

  return (
    <div className="max-w-[1500px] mx-auto space-y-5">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-emerald-700 text-xs font-semibold uppercase tracking-wide mb-1">
            <FaCloud /> Sensorless outputs
          </div>
          <h1 className="text-2xl font-bold text-slate-950">CO2 Devices</h1>
          <p className="text-sm text-slate-500 mt-1">Generated device values for LED panels, independent of telemetry and product sensors.</p>
        </div>
        <div className="flex items-center gap-2">
          <button title="Refresh devices" onClick={loadDevices} className="w-10 h-10 grid place-items-center border border-slate-200 bg-white text-slate-600 hover:text-emerald-700 hover:border-emerald-300 rounded-lg">
            <FaSyncAlt size={13} />
          </button>
          {canManage && <button onClick={openCreate} className="h-10 px-4 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg shadow-sm">
            <FaPlus size={12} /> Add CO2 Device
          </button>}
        </div>
      </header>

      {notice && (
        <div className={`px-4 py-3 border rounded-lg text-sm ${notice.type === "error" ? "bg-red-50 border-red-200 text-red-700" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}>
          {notice.text}
        </div>
      )}

      <section className="grid grid-cols-2 md:grid-cols-4 border border-slate-200 bg-white rounded-lg overflow-hidden">
        <Metric label="Total devices" value={devices.length} />
        <Metric label="Running" value={devices.filter((device) => !device.paused && device.enabled).length} tone="green" />
        <Metric label="Paused" value={devices.filter((device) => device.paused).length} tone="amber" />
        <Metric label="Public API" value="HTTPS" />
      </section>

      <section>
        {loading ? (
          <div className="py-16 text-center text-sm text-slate-500">Loading CO2 devices...</div>
        ) : devices.length === 0 ? (
          <div className="py-16 border border-dashed border-slate-300 bg-white rounded-lg text-center">
            <FaCloud className="mx-auto text-slate-300 mb-3" size={28} />
            <p className="font-semibold text-slate-800">No CO2 devices configured</p>
            <p className="text-sm text-slate-500 mt-1">{canManage ? "Add the first sensorless output device." : "Ask a super admin to allot a CO2 device to you."}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {devices.map((device) => (
              <article key={device.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                <div className="px-4 py-3 flex items-start justify-between gap-3 border-b border-slate-100">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-bold text-slate-900">{device.name}</h2>
                      <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${device.paused ? "bg-amber-50 text-amber-700" : device.enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {device.paused ? "Paused" : device.enabled ? "Running" : "Disabled"}
                      </span>
                    </div>
                    <p className="font-mono text-xs text-slate-500 mt-1">{device.deviceUid}</p>
                  </div>
                  {canManage && <div className="flex items-center gap-1">
                    <IconButton title="Edit device" onClick={() => openEdit(device)} icon={FaEdit} />
                    <IconButton title={device.paused ? "Resume device" : "Pause device"} disabled={busyId === device.id} onClick={() => togglePause(device)} icon={device.paused ? FaPlay : FaPause} />
                    <IconButton danger title="Delete device" disabled={busyId === device.id} onClick={() => deleteDevice(device)} icon={FaTrash} />
                  </div>}
                </div>

                <div className="p-4 grid md:grid-cols-[minmax(0,1fr)_180px] gap-4">
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase font-bold text-slate-400 mb-1.5">LED panel endpoint</p>
                    <button onClick={() => copyUrl(device.deviceUid)} className="w-full min-w-0 flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded text-left hover:border-emerald-300">
                      <code className="text-xs text-emerald-800 truncate flex-1">{PUBLIC_API_BASE}/api/co2/{device.deviceUid}</code>
                      <FaCopy className="text-slate-400 flex-shrink-0" size={12} />
                    </button>
                    {copied === device.deviceUid && <p className="text-[11px] text-emerald-700 mt-1">URL copied</p>}
                    <p className="text-xs text-slate-500 mt-3">
                      {(device.parameters || []).length} parameters
                      {device.schedule?.enabled ? ` · ${device.schedule.startTime}-${device.schedule.endTime}` : " · continuous"}
                    </p>
                  </div>
                  <pre className="bg-slate-950 text-emerald-300 rounded p-3 text-xs leading-5 overflow-hidden whitespace-pre-wrap break-all min-h-[92px]">{JSON.stringify(device.output, null, 2)}</pre>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {editorOpen && (
        <div className="fixed inset-0 z-[1200] bg-slate-950/45 p-3 md:p-6 flex items-center justify-center" onMouseDown={(event) => event.target === event.currentTarget && setEditorOpen(false)}>
          <form onSubmit={saveDevice} className="bg-white w-full max-w-7xl max-h-[94vh] overflow-y-auto rounded-lg shadow-2xl">
            <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-950">{form.id ? "Edit CO2 Device" : "Add CO2 Device"}</h2>
                <p className="text-xs text-slate-500 mt-0.5">Configure generated values and the active time window.</p>
              </div>
              <button type="button" title="Close" onClick={() => setEditorOpen(false)} className="w-9 h-9 grid place-items-center text-slate-500 hover:bg-slate-100 rounded-lg"><FaTimes /></button>
            </div>

            <div className="p-5 space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Device UID">
                  <input required value={form.deviceUid} onChange={(event) => setForm({ ...form, deviceUid: event.target.value.toUpperCase() })} placeholder="BDCB1260004" className="control font-mono" />
                </Field>
                <Field label="Display name">
                  <input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="CO2 Display 04" className="control" />
                </Field>
              </div>

              <div>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <h3 className="font-bold text-slate-900">Output parameters</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Each parameter starts at minimum and reaches maximum over the chosen period, rising by a random amount up to the increment at every update.</p>
                  </div>
                  <button type="button" onClick={() => setForm((current) => ({ ...current, parameters: [...current.parameters, emptyParameter()] }))} className="text-sm font-semibold text-emerald-700 inline-flex items-center gap-1.5"><FaPlus size={11} /> Add parameter</button>
                </div>
                <div className="space-y-3">
                  {form.parameters.map((parameter, index) => (
                    <div key={index} className="border border-slate-200 rounded-lg p-3">
                      <div className="grid sm:grid-cols-2 lg:grid-cols-[minmax(100px,0.6fr)_1fr_1fr_1fr_1fr_1fr_40px] gap-3 items-end">
                        <Field label="JSON field">
                          <input required value={parameter.name} onChange={(event) => updateParameter(index, "name", event.target.value)} placeholder="co2" className="control font-mono" />
                        </Field>
                        <ValueUnit label="Minimum" value={parameter.minValue} unit={parameter.minUnit} onValue={(value) => updateParameter(index, "minValue", value)} onUnit={(value) => updateParameter(index, "minUnit", value)} />
                        <ValueUnit label="Maximum" value={parameter.maxValue} unit={parameter.maxUnit} onValue={(value) => updateParameter(index, "maxValue", value)} onUnit={(value) => updateParameter(index, "maxUnit", value)} />
                        <ValueUnit label="Reach maximum in" value={parameter.periodValue} unit={parameter.periodUnit} options={PERIOD_UNITS} onValue={(value) => updateParameter(index, "periodValue", value)} onUnit={(value) => updateParameter(index, "periodUnit", value)} />
                        <ValueUnit label="Increment 0 to" value={parameter.incrementValue} unit={parameter.incrementUnit} onValue={(value) => updateParameter(index, "incrementValue", value)} onUnit={(value) => updateParameter(index, "incrementUnit", value)} />
                        <ValueUnit label="Update every" value={parameter.intervalValue} unit={parameter.intervalUnit} options={TIME_UNITS} onValue={(value) => updateParameter(index, "intervalValue", value)} onUnit={(value) => updateParameter(index, "intervalUnit", value)} />
                        <button type="button" title="Remove parameter" disabled={form.parameters.length === 1} onClick={() => removeParameter(index)} className="w-10 h-10 grid place-items-center text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-30"><FaTrash size={13} /></button>
                      </div>
                      {updateHint(parameter) && <p className="text-xs text-slate-500 mt-2">{updateHint(parameter)}</p>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-200 pt-5">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div>
                    <h3 className="font-bold text-slate-900">Operating schedule</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Values remain unchanged outside selected hours.</p>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <input type="checkbox" checked={!!form.schedule.enabled} onChange={(event) => setForm({ ...form, schedule: { ...form.schedule, enabled: event.target.checked } })} className="accent-emerald-600" /> Enabled
                  </label>
                </div>
                {form.schedule.enabled && (
                  <div className="grid lg:grid-cols-[1fr_150px_150px_150px] gap-4 items-end">
                    <div>
                      <label className="field-label">Working days</label>
                      <div className="flex gap-1.5 flex-wrap">
                        {DAYS.map((day) => {
                          const active = (form.schedule.workingDays || []).includes(day.value);
                          return <button key={day.value} type="button" onClick={() => toggleDay(day.value)} className={`h-10 min-w-11 px-2 rounded-lg border text-xs font-semibold ${active ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-500"}`}>{day.label}</button>;
                        })}
                      </div>
                    </div>
                    <Field label="From time"><input type="time" value={form.schedule.startTime || "00:00"} onChange={(event) => setForm({ ...form, schedule: { ...form.schedule, startTime: event.target.value } })} className="control" /></Field>
                    <Field label="Till time"><input type="time" value={form.schedule.endTime || "23:59"} onChange={(event) => setForm({ ...form, schedule: { ...form.schedule, endTime: event.target.value } })} className="control" /></Field>
                    <Field label="UTC offset (minutes)"><input type="number" value={form.schedule.utcOffsetMinutes ?? 330} onChange={(event) => setForm({ ...form, schedule: { ...form.schedule, utcOffsetMinutes: event.target.value } })} className="control" /></Field>
                  </div>
                )}
              </div>

              <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={form.enabled} onChange={(event) => setForm({ ...form, enabled: event.target.checked })} className="accent-emerald-600" /> Public endpoint enabled
              </label>
            </div>

            <div className="sticky bottom-0 bg-white px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button type="button" onClick={() => setEditorOpen(false)} className="h-10 px-4 border border-slate-200 text-slate-700 font-semibold text-sm rounded-lg">Cancel</button>
              <button disabled={saving} type="submit" className="h-10 px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm rounded-lg disabled:opacity-50">{saving ? "Saving..." : "Save device"}</button>
            </div>
          </form>
        </div>
      )}

      <style>{`
        .control { width: 100%; height: 40px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 0 10px; background: white; color: #0f172a; font-size: 13px; outline: none; }
        .control:focus { border-color: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,.1); }
        .field-label { display: block; margin-bottom: 6px; color: #64748b; font-size: 11px; font-weight: 700; text-transform: uppercase; }
      `}</style>
    </div>
  );
};

const Field = ({ label, children }) => <label><span className="field-label">{label}</span>{children}</label>;

const ValueUnit = ({ label, value, unit, onValue, onUnit, options = UNITS }) => (
  <div>
    <label className="field-label">{label}</label>
    <div className="grid grid-cols-[1fr_74px]">
      <input required type="number" step="any" value={value} onChange={(event) => onValue(event.target.value)} className="control rounded-r-none" />
      <select value={unit} onChange={(event) => onUnit(event.target.value)} className="control rounded-l-none border-l-0 px-1">{options.map((option) => <option key={option}>{option}</option>)}</select>
    </div>
  </div>
);

const Metric = ({ label, value, tone }) => (
  <div className="px-4 py-3 border-r border-b md:border-b-0 border-slate-100 last:border-r-0">
    <p className="text-[10px] uppercase font-bold text-slate-400">{label}</p>
    <p className={`text-lg font-bold mt-0.5 ${tone === "green" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : "text-slate-900"}`}>{value}</p>
  </div>
);

const IconButton = ({ icon: Icon, title, onClick, disabled, danger }) => (
  <button type="button" title={title} onClick={onClick} disabled={disabled} className={`w-9 h-9 grid place-items-center rounded-lg disabled:opacity-40 ${danger ? "text-red-500 hover:bg-red-50" : "text-slate-500 hover:bg-slate-100 hover:text-emerald-700"}`}>
    <Icon size={13} />
  </button>
);

export default Co2Devices;
